/**
 * DocumentViewPro - Professional PDF Viewer
 *
 * Features:
 * - PDF.js rendering with zoom controls (25%-400%)
 * - Interactive block overlays (copy text, export excel, copy image)
 * - Lightning AI Q&A with Gemini Flash
 * - Search within PDF
 * - Fit to width/page modes
 * - Direct PDF download
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Snackbar, IconButton, Tooltip, Typography, Chip } from '@mui/material';
import { BugReport as DebugIcon } from '@mui/icons-material';
import {
  ContentCopy as CopyIcon,
  TableChart as ExcelIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import ViewerToolbar from '../components/docviewer/ViewerToolbar';
import ViewerSidebar from '../components/docviewer/ViewerSidebar';
import SearchPanel from '../components/docviewer/SearchPanel';
import TOCSidebar, { TocEntry } from '../components/docviewer/TOCSidebar';
import { Block, DocumentData } from '../components/docviewer/types';
import { exportTableToExcelEnhanced } from '../utils/excelExportEnhanced';
import { useLanguage } from '../contexts/LanguageContext';

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

const API_BASE = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8001'
  : 'https://api.wallst.co.il';
const API_KEY = process.env.REACT_APP_API_KEY || 'psagot-api-key-2024-secure';

// Normalize bbox to array format [x, y, x2, y2]
// Handles both array format [x, y, x2, y2] and object format {x, y, width, height, page}
function normalizeBbox(bbox: any): [number, number, number, number] | null {
  if (!bbox) return null;

  // Already array format
  if (Array.isArray(bbox) && bbox.length >= 4) {
    return [bbox[0], bbox[1], bbox[2], bbox[3]];
  }

  // Object format {x, y, width, height}
  if (typeof bbox === 'object' && 'x' in bbox && 'y' in bbox) {
    const x = bbox.x || 0;
    const y = bbox.y || 0;
    const width = bbox.width || 0;
    const height = bbox.height || 0;
    return [x, y, x + width, y + height];
  }

  return null;
}

// Flatten nested blocks into flat array (deduplicated by block ID)
// PRIORITY ORDER (critical for highlighting to work):
// 0. Handle already-flat array format (from API /documents/{id} endpoint)
// 1. Process 'blocks' array FIRST - this has ref_id needed for citation highlighting
// 2. Process 'pages' structure SECOND - supplements with any missing blocks
function flattenBlocks(layoutData: any): Block[] {
  const blockMap = new Map<string, Block>();

  // STEP 0: Handle already-flat array format (API returns transformed flat array)
  if (Array.isArray(layoutData)) {
    layoutData.forEach((block: any) => processBlockToMap(block, blockMap));
    return Array.from(blockMap.values());
  }

  // STEP 1: Process 'blocks' array FIRST (has ref_id for highlighting)
  if (layoutData && typeof layoutData === 'object' && 'blocks' in layoutData && Array.isArray(layoutData.blocks)) {
    layoutData.blocks.forEach((block: any) => processBlockToMap(block, blockMap));
  }

  // STEP 2: Process nested 'pages' structure for any missing blocks
  function traversePages(node: any): void {
    if (!node) return;

    // Handle array input
    if (Array.isArray(node)) {
      node.forEach(n => traversePages(n));
      return;
    }

    if (typeof node !== 'object') return;

    // Process this node if it has block data (only adds if not already in map)
    processBlockToMap(node, blockMap);

    // Recurse into children
    if (node.children) {
      traversePages(node.children);
    }

    // Recurse into pages
    if (node.pages) {
      if (Array.isArray(node.pages)) {
        node.pages.forEach((page: any) => traversePages(page));
      } else if (typeof node.pages === 'object') {
        // Handle {children: [...]} format
        if (node.pages.children) {
          traversePages(node.pages.children);
        } else if (node.pages.pages && node.pages.pages.children) {
          // Handle nested {pages: {pages: {children: [...]}}} format
          traversePages(node.pages.pages.children);
        } else {
          Object.values(node.pages).forEach((page: any) => traversePages(page));
        }
      }
    }
  }

  // Traverse pages structure for supplemental blocks (not already in blocks array)
  if (layoutData && typeof layoutData === 'object') {
    traversePages(layoutData.pages);
  }

  return Array.from(blockMap.values());
}

// Process block into map (deduplicates by ID, first occurrence wins)
function processBlockToMap(node: any, map: Map<string, Block>): void {
  if (!node || typeof node !== 'object') return;

  const blockId = node.block_id || node.id;
  const blockType = normalizeBlockType(node.block_type);

  // Skip if already processed or if it's a Page block
  if (!blockId || !node.bbox || blockType === 'Page' || map.has(blockId)) return;

  const normalizedBbox = normalizeBbox(node.bbox);
  if (!normalizedBbox) return;

  // Extract page number
  let pageNum = node.page_number ?? node.page;
  if (pageNum === undefined && typeof node.bbox === 'object' && 'page' in node.bbox) {
    pageNum = node.bbox.page;
  }
  if (pageNum === undefined) {
    const pageMatch = blockId.match(/\/page\/(\d+)/);
    if (pageMatch) pageNum = parseInt(pageMatch[1], 10);
  }

  map.set(blockId, {
    ...node,
    id: blockId,
    block_type: blockType,
    bbox: normalizedBbox,
    page: pageNum
  } as Block);
}

// Normalize block_type to TitleCase (e.g., 'text' -> 'Text', 'table' -> 'Table')
function normalizeBlockType(blockType: string): string {
  if (!blockType) return blockType;
  return blockType.charAt(0).toUpperCase() + blockType.slice(1).toLowerCase();
}

interface DocumentViewProProps {
  docIdProp?: string;
}

const DocumentViewPro: React.FC<DocumentViewProProps> = ({ docIdProp }) => {
  const [searchParams] = useSearchParams();
  const docId = docIdProp || searchParams.get('id') || searchParams.get('docId');
  const highlightBlockId = searchParams.get('highlightBlock');
  const highlightRefId = searchParams.get('highlightRef');  // ref_id based highlighting
  const highlightRefs = searchParams.get('highlightRefs');  // Multiple refs (comma-separated) for multi-block chunks
  const initialPage = searchParams.get('page');  // Jump to page
  const { t } = useLanguage();

  // Document state
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);

  // View state
  const [currentPage, setCurrentPage] = useState(() => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) : 1;
  });
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [fitMode, setFitMode] = useState<'width' | 'page' | 'none'>('width');
  const [viewMode, setViewMode] = useState<'pdf' | 'grid' | 'liquid'>('pdf');
  const [scale, setScale] = useState(1.5);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Panel state
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [inspectedBlock, setInspectedBlock] = useState<Block | null>(null);
  const [tocOpen, setTocOpen] = useState(false);
  const [tocData, setTocData] = useState<TocEntry[]>([]);
  const [citationOffScreen, setCitationOffScreen] = useState(false);

  // Hover toolbar state
  const [hoveredBlock, setHoveredBlock] = useState<Block | null>(null);
  const [toolbarPos, setToolbarPos] = useState<{ top: number; left: number } | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<any>(null);
  const renderTaskRef = useRef<any>(null);
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch document
  useEffect(() => {
    if (!docId) return;

    const fetchDocument = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/v3/documents/${docId}`, {
          headers: { 'X-API-Key': API_KEY },
        });
        if (!response.ok) throw new Error('Document not found');
        const data = await response.json();
        setDocument(data);

        if (data.layout_data) {
          const flatBlocks = flattenBlocks(data.layout_data);

          // Debug: Log block coordinates and ref_id
          const page0Blocks = flatBlocks.filter(b => b.page === 0);
          if (page0Blocks.length > 0) {
            console.log('[DEBUG] Total blocks:', flatBlocks.length);
            console.log('[DEBUG] Page 0 blocks:', page0Blocks.length);
            // Check ref_id on first few blocks - log as string for visibility
            const listItems = flatBlocks.filter(b => b.block_type?.toLowerCase() === 'listitem').slice(0, 3);
            listItems.forEach((b, i) => {
              console.log(`[DEBUG] Processed ListItem ${i}: id=${b.id} ref_id=${(b as any).ref_id} meaning=${!!(b as any).meaning}`);
            });
            // Also log raw layout_data structure
            if (Array.isArray(data.layout_data)) {
              const rawListItems = data.layout_data.filter((b: any) => b.block_type?.toLowerCase() === 'listitem').slice(0, 3);
              rawListItems.forEach((b: any, i: number) => {
                console.log(`[DEBUG] Raw API ListItem ${i}: id=${b.id} ref_id=${b.ref_id} meaning=${!!b.meaning}`);
              });
            }
          }

          setBlocks(flatBlocks);
        }

        // Parse TOC data from toc_json
        if (data.toc_json) {
          try {
            const toc = typeof data.toc_json === 'string' ? JSON.parse(data.toc_json) : data.toc_json;
            const catalog = toc.block_catalog || toc.sections || [];
            const entries: TocEntry[] = catalog
              .filter((item: any) => item.title || item.heading)
              .map((item: any) => ({
                title: item.title || item.heading || '',
                page: item.page ?? item.page_number ?? 0,
                level: item.level ?? item.heading_level ?? 0,
                ref_id: item.ref_id,
                sentiment: item.sentiment,
              }));
            setTocData(entries);
          } catch (e) {
            console.warn('Failed to parse TOC data:', e);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  // Load PDF document
  useEffect(() => {
    if (!document?.storage_url || !window.pdfjsLib) return;

    let cancelled = false;

    const loadPdf = async () => {
      try {
        // Destroy previous document if exists
        if (pdfDocRef.current) {
          pdfDocRef.current.destroy();
          pdfDocRef.current = null;
        }

        const pdf = await window.pdfjsLib.getDocument(document.storage_url).promise;

        if (cancelled) {
          pdf.destroy();
          return;
        }

        pdfDocRef.current = pdf;
        setTotalPages(pdf.numPages);
      } catch (err) {
        if (!cancelled) {
          console.error('PDF load error:', err);
          setError('Failed to load PDF');
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [document?.storage_url]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, []);

  // Resolve highlight targets: either by block_id, ref_id, or multi-ref list
  // Returns a Set of ALL matching block IDs so we highlight the entire cited region
  const resolvedHighlightBlockIds = React.useMemo(() => {
    const ids = new Set<string>();

    if (highlightBlockId) {
      ids.add(highlightBlockId);
      return ids;
    }

    // Try highlightRefs (comma-separated list of refs from aggregated chunks)
    // Highlight ALL matching blocks, not just the first one
    if (highlightRefs && blocks.length > 0) {
      const refList = highlightRefs.split(',').map(r => r.trim()).filter(r => r);
      for (const refId of refList) {
        const targetBlock = blocks.find(b => String(b.ref_id) === String(refId));
        if (targetBlock) {
          ids.add(targetBlock.id);
        }
      }
      if (ids.size > 0) {
        console.log(`Resolved highlightRefs - found ${ids.size} blocks for refs:`, refList);
      } else {
        console.log('No block found for any of highlightRefs:', refList);
      }
      return ids;
    }

    // Fallback to single highlightRef
    if (highlightRefId && blocks.length > 0) {
      const targetBlock = blocks.find(b => String(b.ref_id) === String(highlightRefId));
      if (targetBlock) {
        console.log('Resolved highlightRef', highlightRefId, 'to block', targetBlock.id);
        ids.add(targetBlock.id);
      } else {
        console.log('Block not found for highlightRef:', highlightRefId);
      }
    }
    return ids;
  }, [highlightBlockId, highlightRefId, highlightRefs, blocks]);

  // First highlighted block ID — used for scroll/navigation target
  const firstHighlightBlockId = React.useMemo(() => {
    if (resolvedHighlightBlockIds.size === 0) return null;
    return resolvedHighlightBlockIds.values().next().value ?? null;
  }, [resolvedHighlightBlockIds]);

  // Navigate to highlighted block when URL has highlightBlock or highlightRef param
  // Track if we've already navigated to avoid re-triggering on currentPage change
  const hasNavigatedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!firstHighlightBlockId || blocks.length === 0) return;

    // Avoid re-navigating if we've already processed this block
    if (hasNavigatedRef.current === firstHighlightBlockId) return;

    // Find the block to scroll to (first highlighted block)
    const targetBlock = blocks.find(b => b.id === firstHighlightBlockId);
    if (!targetBlock) {
      console.log('Highlight block not found:', firstHighlightBlockId);
      return;
    }

    // Get page number from block (preferred) or URL param (fallback)
    // IMPORTANT: block.page is 0-indexed, but currentPage is 1-indexed
    let targetPage: number | undefined;

    if (targetBlock.page !== undefined && targetBlock.page !== null) {
      targetPage = targetBlock.page + 1;  // Convert 0-indexed to 1-indexed
      console.log('Block page (0-indexed):', targetBlock.page, '-> targetPage (1-indexed):', targetPage);
    }

    // If block doesn't have page, try to extract from block ID (e.g., /page/7/Table/123)
    if (!targetPage && targetBlock.id) {
      const pageMatch = targetBlock.id.match(/\/page\/(\d+)\//);
      if (pageMatch) {
        targetPage = parseInt(pageMatch[1], 10) + 1; // Pages are 0-indexed in ID
        console.log('Extracted page from block ID:', targetPage);
      }
    }

    // Only use URL page as last resort (when block has no page info)
    // Don't let URL page override block's actual page - the block knows where it is
    if (!targetPage && initialPage) {
      targetPage = parseInt(initialPage, 10);
      console.log('Using URL page as fallback:', targetPage);
    }

    if (targetPage && targetPage !== currentPage) {
      console.log('Navigating to page', targetPage, 'for', resolvedHighlightBlockIds.size, 'highlighted blocks');
      hasNavigatedRef.current = firstHighlightBlockId;
      setCurrentPage(targetPage);
    } else if (targetPage === currentPage) {
      // Already on correct page, just mark as navigated
      hasNavigatedRef.current = firstHighlightBlockId;
    }

    // Scroll to first highlighted block after a short delay (to let the page render)
    const scrollTimer = setTimeout(() => {
      const blockElement = window.document?.querySelector(`[data-block-id="${firstHighlightBlockId}"]`);
      if (blockElement) {
        blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        console.log('Scrolled to block:', firstHighlightBlockId, `(${resolvedHighlightBlockIds.size} blocks highlighted)`);
      } else {
        console.log('Block element not found in DOM:', firstHighlightBlockId);
      }
    }, 500);

    return () => clearTimeout(scrollTimer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstHighlightBlockId, blocks, initialPage]);  // currentPage excluded to prevent infinite loop

  // IntersectionObserver to detect when highlighted blocks scroll out of view
  useEffect(() => {
    if (resolvedHighlightBlockIds.size === 0) {
      setCitationOffScreen(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const anyVisible = entries.some(e => e.isIntersecting);
        setCitationOffScreen(!anyVisible);
      },
      { threshold: 0.1 }
    );

    // Observe after a delay to let blocks render
    const timer = setTimeout(() => {
      resolvedHighlightBlockIds.forEach(blockId => {
        const el = window.document.querySelector(`[data-block-id="${blockId}"]`);
        if (el) observer.observe(el);
      });
    }, 800);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [resolvedHighlightBlockIds, currentPage]);

  // Render current page
  useEffect(() => {
    if (!pdfDocRef.current || !canvasRef.current || !containerRef.current) return;

    let cancelled = false;

    const renderPage = async () => {
      try {
        // Cancel any pending render
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const pdf = pdfDocRef.current;
        if (!pdf) return;

        const page = await pdf.getPage(currentPage);

        if (cancelled) return;

        const baseViewport = page.getViewport({ scale: 1.0 });

        // Calculate scale based on fit mode
        let calcScale = zoom / 100;
        const containerWidth = containerRef.current?.clientWidth || 800;
        const containerHeight = containerRef.current?.clientHeight || 600;

        if (fitMode === 'width') {
          calcScale = (containerWidth - 80) / baseViewport.width;
        } else if (fitMode === 'page') {
          const scaleW = (containerWidth - 80) / baseViewport.width;
          const scaleH = (containerHeight - 80) / baseViewport.height;
          calcScale = Math.min(scaleW, scaleH);
        }

        // Apply zoom on top of fit
        if (fitMode !== 'none') {
          calcScale *= zoom / 100;
        }

        setScale(calcScale);
        const viewport = page.getViewport({ scale: calcScale });

        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        setCanvasSize({ width: viewport.width, height: viewport.height });

        const context = canvas.getContext('2d');
        if (!context) return;

        renderTaskRef.current = page.render({ canvasContext: context, viewport });

        await renderTaskRef.current.promise;
        renderTaskRef.current = null;

      } catch (err: any) {
        if (err?.name === 'RenderingCancelledException' || cancelled) {
          // Expected when switching pages quickly
          return;
        }
        console.error('PDF render error:', err);
      }
    };

    renderPage();

    return () => {
      cancelled = true;
    };
  }, [currentPage, zoom, fitMode, totalPages]);

  // Get ALL blocks for current page (for debugging)
  // block.page is 0-indexed (normalized in flattenBlocks), currentPage is 1-indexed
  const allPageBlocks = blocks.filter(block => {
    return block.page === currentPage - 1;
  });

  // Get visible blocks for current page (filtered for overlays)
  const currentPageBlocks = allPageBlocks.filter(block => {
    // Skip TableRow/TableCell - these are children of Table
    if (block.block_type === 'TableRow' || block.block_type === 'TableCell') return false;
    // Skip blocks without bbox
    if (!block.bbox || block.bbox.length < 4) return false;
    // Include all other blocks (Tables, Figures, Text, List, etc.)
    return true;
  });

  // Block type counts for debug panel
  const blockTypeCounts = allPageBlocks.reduce((acc: Record<string, number>, b) => {
    acc[b.block_type] = (acc[b.block_type] || 0) + 1;
    return acc;
  }, {});

  // Block mouse handlers
  const handleBlockMouseEnter = useCallback((block: Block, rect: DOMRect) => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    showTimerRef.current = setTimeout(() => {
      setToolbarPos({ top: rect.top + 5, left: rect.right - 130 });
      setHoveredBlock(block);
    }, 80);
  }, []);

  const handleBlockMouseLeave = useCallback(() => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setHoveredBlock(null);
      setToolbarPos(null);
    }, 400);
  }, []);

  // Clean text helper
  const cleanText = useCallback((str: string): string => {
    const temp = window.document.createElement('div');
    temp.innerHTML = str.replace(/<br\s*\/?>/gi, ' ').replace(/\n/g, ' ');
    return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
  }, []);

  // Copy text
  const handleCopyText = useCallback(async () => {
    if (!hoveredBlock) return;
    const text = hoveredBlock.figure_caption || hoveredBlock.caption || cleanText(hoveredBlock.html || hoveredBlock.text || '');
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: t('viewer.textCopied') });
    } catch {
      setSnackbar({ open: true, message: t('common.failed') });
    }
  }, [hoveredBlock, cleanText, t]);

  // Export Excel
  const handleExportExcel = useCallback(() => {
    if (!hoveredBlock) return;
    try {
      const parts = hoveredBlock.id.split('/');
      const filename = `Page${Number(parts[2]) + 1}_Table${Number(parts[4]) + 1}.xlsx`;
      exportTableToExcelEnhanced(hoveredBlock as any, filename);
      setSnackbar({ open: true, message: t('viewer.tableExported') });
    } catch {
      setSnackbar({ open: true, message: t('common.failed') });
    }
  }, [hoveredBlock, t]);

  // Copy image
  const handleCopyImage = useCallback(async () => {
    if (!hoveredBlock || !canvasRef.current) return;
    const [x1, y1, x2, y2] = hoveredBlock.bbox;
    try {
      const cropCanvas = window.document.createElement('canvas');
      const ctx = cropCanvas.getContext('2d')!;
      cropCanvas.width = (x2 - x1) * scale;
      cropCanvas.height = (y2 - y1) * scale;
      ctx.drawImage(canvasRef.current, x1 * scale, y1 * scale, cropCanvas.width, cropCanvas.height, 0, 0, cropCanvas.width, cropCanvas.height);

      try {
        const blob = await new Promise<Blob>((resolve, reject) => cropCanvas.toBlob(b => b ? resolve(b) : reject(), 'image/png'));
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        setSnackbar({ open: true, message: t('viewer.imageCopied') });
      } catch {
        const link = window.document.createElement('a');
        link.download = `figure_page${currentPage}.png`;
        link.href = cropCanvas.toDataURL('image/png');
        link.click();
        setSnackbar({ open: true, message: t('viewer.imageDownloaded') });
      }
    } catch {
      setSnackbar({ open: true, message: t('common.failed') });
    }
  }, [hoveredBlock, scale, currentPage, t]);

  // Navigate to page from search/AI
  const handleNavigateToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Fullscreen
  const handleFullscreen = useCallback(() => {
    if (containerRef.current) {
      if (window.document.fullscreenElement) {
        window.document.exitFullscreen();
      } else {
        containerRef.current.requestFullscreen();
      }
    }
  }, []);

  const isTable = hoveredBlock?.block_type === 'Table';
  const isImage = hoveredBlock?.block_type === 'Figure' || hoveredBlock?.block_type === 'Picture';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', bgcolor: '#1e1e1e' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', bgcolor: '#1e1e1e' }}>
      {/* Toolbar */}
      <ViewerToolbar
        currentPage={currentPage}
        totalPages={totalPages}
        zoom={zoom}
        fitMode={fitMode}
        viewMode={viewMode}
        pdfUrl={document?.storage_url || ''}
        filename={document?.filename || 'document.pdf'}
        onPageChange={setCurrentPage}
        onZoomChange={setZoom}
        onFitModeChange={setFitMode}
        onViewModeChange={setViewMode}
        onSearchOpen={() => setSearchOpen(true)}
        onAIPanelOpen={() => setAiPanelOpen(true)}
        onFullscreen={handleFullscreen}
        onTOCOpen={() => setTocOpen(true)}
      />

      {/* Main PDF Area */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 3,
          bgcolor: '#525659'
        }}
      >
        {/* Page wrapper - contains canvas and overlays */}
        <Box
          sx={{
            position: 'relative',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            bgcolor: 'white',
            width: canvasSize.width || 'auto',
            height: canvasSize.height || 'auto',
          }}
        >
          {/* PDF Canvas */}
          <canvas
            ref={canvasRef}
            style={{
              display: 'block',
            }}
          />

          {/* Block overlays - positioned absolute over canvas */}
          {currentPageBlocks.map(block => {
            const [x, y, x2, y2] = block.bbox;
            const isHighlighted = resolvedHighlightBlockIds.has(block.id);

            return (
              <Box
                key={block.id}
                data-block-id={block.id}
                onMouseEnter={(e) => handleBlockMouseEnter(block, e.currentTarget.getBoundingClientRect())}
                onMouseLeave={handleBlockMouseLeave}
                onClick={() => {
                  // Open debug panel and show this block
                  if (!debugOpen) setDebugOpen(true);
                  setInspectedBlock(inspectedBlock?.id === block.id ? null : block);
                }}
                sx={{
                  position: 'absolute',
                  left: x * scale,
                  top: y * scale,
                  width: (x2 - x) * scale,
                  height: (y2 - y) * scale,
                  cursor: 'pointer',
                  border: isHighlighted
                    ? '2px solid #fdd835'
                    : block.block_type === 'Table'
                      ? '1px dashed rgba(255,152,0,0.4)'
                      : (block.block_type === 'Figure' || block.block_type === 'Picture')
                        ? '1px dashed rgba(156,39,176,0.4)'
                        : '1px solid transparent',
                  backgroundColor: isHighlighted ? 'rgba(255,235,59,0.3)' : 'transparent',
                  transition: 'all 0.1s',
                  '&:hover': {
                    backgroundColor: block.block_type === 'Table'
                      ? 'rgba(255,152,0,0.15)'
                      : (block.block_type === 'Figure' || block.block_type === 'Picture')
                        ? 'rgba(156,39,176,0.15)'
                        : 'rgba(0,102,204,0.1)',
                    borderColor: block.block_type === 'Table' ? '#ff9800' : (block.block_type === 'Figure' || block.block_type === 'Picture') ? '#9c27b0' : '#0066cc',
                    borderWidth: '2px',
                    borderStyle: 'solid'
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Hover Toolbar */}
      {hoveredBlock && toolbarPos && (
        <Box
          onMouseEnter={() => hideTimerRef.current && clearTimeout(hideTimerRef.current)}
          onMouseLeave={() => { hideTimerRef.current = setTimeout(() => { setHoveredBlock(null); setToolbarPos(null); }, 200); }}
          sx={{
            position: 'fixed',
            top: toolbarPos.top,
            left: toolbarPos.left,
            bgcolor: '#333',
            borderRadius: 1,
            p: 0.5,
            display: 'flex',
            gap: 0.5,
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
          }}
        >
          {isTable ? (
            <>
              <Tooltip title="Export to Excel">
                <IconButton size="small" onClick={handleExportExcel} sx={{ color: '#4caf50' }}>
                  <ExcelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy Text">
                <IconButton size="small" onClick={handleCopyText} sx={{ color: 'white' }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : isImage ? (
            <>
              <Tooltip title="Copy Image">
                <IconButton size="small" onClick={handleCopyImage} sx={{ color: 'white' }}>
                  <ImageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {(hoveredBlock.figure_caption || hoveredBlock.caption) && (
                <Tooltip title="Copy Caption">
                  <IconButton size="small" onClick={handleCopyText} sx={{ color: 'white' }}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ) : (
            <Tooltip title="Copy Text">
              <IconButton size="small" onClick={handleCopyText} sx={{ color: 'white' }}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Block Inspector Panel */}
      {debugOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 64,
            right: 0,
            width: 450,
            height: 'calc(100vh - 64px)',
            bgcolor: '#1a1a1a',
            borderLeft: '1px solid #333',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>
              Block Inspector - Page {currentPage}
            </Typography>
            <IconButton size="small" onClick={() => setDebugOpen(false)} sx={{ color: '#888' }}>✕</IconButton>
          </Box>

          {/* Summary Stats */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #333', bgcolor: '#252525' }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Chip label={`Total: ${allPageBlocks.length}`} size="small" sx={{ bgcolor: '#333', color: 'white' }} />
              <Chip label={`Visible: ${currentPageBlocks.length}`} size="small" color="success" />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {Object.entries(blockTypeCounts).map(([type, count]) => (
                <Chip
                  key={type}
                  label={`${type}: ${count}`}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 22,
                    bgcolor: type === 'Table' ? '#ff9800' : type === 'Figure' ? '#9c27b0' : type === 'Text' ? '#2196f3' : type === 'List' ? '#4caf50' : '#555',
                    color: 'white'
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Block List */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            <Typography variant="caption" sx={{ color: '#888', mb: 1, display: 'block' }}>
              Click a block to inspect details:
            </Typography>
            {allPageBlocks.map((block) => {
              const isVisible = currentPageBlocks.includes(block);
              const isSelected = inspectedBlock?.id === block.id;
              return (
                <Box
                  key={block.id}
                  onClick={() => setInspectedBlock(isSelected ? null : block)}
                  sx={{
                    p: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #4caf50' : '1px solid #333',
                    bgcolor: isSelected ? 'rgba(76,175,80,0.2)' : isVisible ? 'rgba(255,255,255,0.03)' : 'rgba(244,67,54,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                    <Chip
                      label={block.block_type}
                      size="small"
                      sx={{
                        fontSize: '0.65rem',
                        height: 20,
                        bgcolor: block.block_type === 'Table' ? '#ff9800' : block.block_type === 'Figure' ? '#9c27b0' : block.block_type === 'Text' ? '#2196f3' : '#555'
                      }}
                    />
                    <Typography variant="caption" sx={{ color: isVisible ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                      {isVisible ? '✓' : '✗'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem' }}>
                      {block.id.split('/').slice(-2).join('/')}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#aaa', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(block.text || block.html || '').replace(/<[^>]+>/g, '').substring(0, 80) || '(no content)'}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Selected Block Details */}
          {inspectedBlock && (
            <Box sx={{ borderTop: '1px solid #333', bgcolor: '#252525', maxHeight: '40%', overflow: 'auto' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ color: '#4caf50' }}>Block Details</Typography>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(JSON.stringify(inspectedBlock, null, 2))} sx={{ color: '#888', p: 0 }}>
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Box sx={{ p: 1.5, fontFamily: 'monospace', fontSize: '0.7rem', color: '#ccc' }}>
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>ID:</strong> {inspectedBlock.id}</Box>
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Type:</strong> {inspectedBlock.block_type}</Box>
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>BBox:</strong> [{inspectedBlock.bbox?.map(n => Math.round(n)).join(', ')}]</Box>
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Has Text:</strong> {inspectedBlock.text ? `Yes (${inspectedBlock.text.length} chars)` : 'No'}</Box>
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Has HTML:</strong> {inspectedBlock.html ? `Yes (${inspectedBlock.html.length} chars)` : 'No'}</Box>
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Children:</strong> {inspectedBlock.children?.length || 0}</Box>
                {inspectedBlock.children && inspectedBlock.children.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <strong style={{ color: '#888' }}>Child Types:</strong>{' '}
                    {Array.from(new Set(inspectedBlock.children.map(c => c.block_type))).join(', ')}
                  </Box>
                )}
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Ref ID:</strong> {(inspectedBlock as any).ref_id ?? 'None'}</Box>
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Page:</strong> {(inspectedBlock as any).page ?? 'N/A'}</Box>
                {(inspectedBlock as any).caption && (
                  <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Caption:</strong> {(inspectedBlock as any).caption}</Box>
                )}
                {(inspectedBlock as any).figure_caption && (
                  <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Figure Caption:</strong> {(inspectedBlock as any).figure_caption}</Box>
                )}
                {(inspectedBlock as any).figure_number && (
                  <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Figure Number:</strong> {(inspectedBlock as any).figure_number}</Box>
                )}
                {(inspectedBlock as any).items && (inspectedBlock as any).items.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <strong style={{ color: '#888' }}>List Items ({(inspectedBlock as any).items.length}):</strong>
                    <Box sx={{ mt: 0.5, pl: 1, fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                      {(inspectedBlock as any).items.map((item: string, idx: number) => (
                        <Box key={idx} sx={{ color: item.startsWith('  ') ? '#aaa' : '#ddd', mb: 0.25 }}>
                          {item.startsWith('    ') ? '    - ' + item.trim() : item.startsWith('  ') ? '  - ' + item.trim() : '- ' + item.trim()}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                <Box sx={{ mb: 1 }}><strong style={{ color: '#888' }}>Structured Data:</strong> {(inspectedBlock as any).structured_data ? 'Yes' : 'No'}</Box>
                {/* Phase 8: Semantic Meaning */}
                {inspectedBlock.meaning && (
                  <Box sx={{ mt: 1, mb: 1, p: 1, bgcolor: '#1e3a1e', borderRadius: 1, border: '1px solid #4caf50' }}>
                    <strong style={{ color: '#4caf50' }}>Semantic Meaning:</strong>
                    {inspectedBlock.meaning.main_idea && (
                      <Box sx={{ mt: 0.5 }}><strong style={{ color: '#888' }}>Main Idea:</strong> {inspectedBlock.meaning.main_idea}</Box>
                    )}
                    {inspectedBlock.meaning.section_context && (
                      <Box sx={{ mt: 0.5 }}><strong style={{ color: '#888' }}>Section Context:</strong> {inspectedBlock.meaning.section_context}</Box>
                    )}
                    {inspectedBlock.meaning.topic && (
                      <Box sx={{ mt: 0.5 }}><strong style={{ color: '#888' }}>Topic:</strong> {inspectedBlock.meaning.topic}</Box>
                    )}
                    {inspectedBlock.meaning.emphasis_phrases && inspectedBlock.meaning.emphasis_phrases.length > 0 && (
                      <Box sx={{ mt: 0.5 }}><strong style={{ color: '#888' }}>Emphasis:</strong> {inspectedBlock.meaning.emphasis_phrases.join(', ')}</Box>
                    )}
                    {inspectedBlock.meaning.sub_ideas && inspectedBlock.meaning.sub_ideas.length > 0 && (
                      <Box sx={{ mt: 0.5 }}><strong style={{ color: '#888' }}>Sub-ideas:</strong> {inspectedBlock.meaning.sub_ideas.slice(0, 5).join('; ')}</Box>
                    )}
                  </Box>
                )}
                {inspectedBlock.text && (
                  <Box sx={{ mt: 2 }}>
                    <strong style={{ color: '#888' }}>Text Preview:</strong>
                    <Box sx={{ mt: 0.5, p: 1, bgcolor: '#1a1a1a', borderRadius: 1, maxHeight: 100, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {inspectedBlock.text.substring(0, 500)}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Debug Panel Toggle Button */}
      <Box sx={{ position: 'fixed', bottom: 16, right: debugOpen ? 466 : 16, zIndex: 1001, transition: 'right 0.2s' }}>
        <Tooltip title={debugOpen ? 'Close Inspector' : 'Open Block Inspector'}>
          <IconButton
            onClick={() => { setDebugOpen(!debugOpen); setInspectedBlock(null); }}
            sx={{ bgcolor: debugOpen ? '#4caf50' : '#666', color: 'white', '&:hover': { bgcolor: debugOpen ? '#388e3c' : '#888' } }}
          >
            <DebugIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* AI Chat Sidebar */}
      <ViewerSidebar
        open={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        documentId={docId || ''}
        currentPage={currentPage}
        onNavigateToPage={handleNavigateToPage}
      />

      {/* Search Panel */}
      <SearchPanel
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        blocks={blocks}
        onNavigate={handleNavigateToPage}
      />

      {/* TOC Sidebar */}
      <TOCSidebar
        open={tocOpen}
        onClose={() => setTocOpen(false)}
        tocData={tocData}
        currentPage={currentPage}
        onNavigateToPage={handleNavigateToPage}
      />

      {/* Jump to Citation floating chip */}
      {citationOffScreen && resolvedHighlightBlockIds.size > 0 && (
        <Chip
          label="Jump to Citation"
          onClick={() => {
            const firstId = resolvedHighlightBlockIds.values().next().value;
            if (firstId) {
              const el = window.document.querySelector(`[data-block-id="${firstId}"]`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            bgcolor: '#fdd835',
            color: '#333',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            '&:hover': { bgcolor: '#fbc02d' },
          }}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DocumentViewPro;
